"use client";

// Contribute a photograph of one park.
//
// The park is fixed by the route, not chosen here, so a submission cannot be
// orphaned or filed against the wrong park by a mis-click in a dropdown.
//
// Three fields need more than a text input, and each is asked for a reason:
// where the photographer stood, which way the camera pointed, and an explicit
// licence grant. A photograph of a park without a viewpoint is decoration; with
// one it is evidence about a place, and a reviewer can check it matches.

import { useEffect, useRef, useState } from "react";
import * as maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { Field } from "@/components/ui/field";
import { cardinal } from "@/lib/bearing";

const INPUT_CLASS =
  "h-10 w-full rounded-md border border-outline-variant bg-surface-container-lowest px-3 font-body-md text-body-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-60";

type State =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "done"; message: string }
  | { kind: "error"; message: string };

/**
 * Read GPS and heading out of a JPEG's EXIF, if it has any.
 *
 * Deliberately small and deliberately optional. Phones and messaging apps strip
 * EXIF routinely, so this is a convenience that saves the contributor some
 * dragging, never the mechanism: everything it finds is shown in the form for
 * them to confirm or correct, and the form works identically when it finds
 * nothing. That is also why there is no EXIF library here for 30 lines of work.
 */
export function readExif(buf: ArrayBuffer): { lat?: number; lon?: number; bearing?: number; date?: string } {
  const view = new DataView(buf);
  if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return {};

  // Walk the JPEG segments for APP1 (Exif).
  let offset = 2;
  let tiff = -1;
  while (offset + 4 < view.byteLength) {
    if (view.getUint8(offset) !== 0xff) break;
    const marker = view.getUint8(offset + 1);
    const size = view.getUint16(offset + 2);
    if (marker === 0xe1) { tiff = offset + 10; break; }
    if (marker === 0xda) break; // start of scan: no more metadata
    offset += 2 + size;
  }
  if (tiff < 0 || tiff + 8 > view.byteLength) return {};

  const le = view.getUint16(tiff) === 0x4949;
  const u16 = (o: number) => view.getUint16(o, le);
  const u32 = (o: number) => view.getUint32(o, le);
  const rational = (o: number) => u32(o) / (u32(o + 4) || 1);

  const out: { lat?: number; lon?: number; bearing?: number; date?: string } = {};
  const readDir = (dirStart: number, into: (tag: number, type: number, count: number, valueAt: number) => void) => {
    if (dirStart + 2 > view.byteLength) return;
    const n = u16(dirStart);
    for (let i = 0; i < n; i++) {
      const entry = dirStart + 2 + i * 12;
      if (entry + 12 > view.byteLength) return;
      const tag = u16(entry);
      const type = u16(entry + 2);
      const count = u32(entry + 4);
      // Values of four bytes or fewer sit inline; longer ones are a pointer.
      const size = ({ 1: 1, 2: 1, 3: 2, 4: 4, 5: 8, 10: 8 } as Record<number, number>)[type] ?? 1;
      const valueAt = size * count <= 4 ? entry + 8 : tiff + u32(entry + 8);
      into(tag, type, count, valueAt);
    }
  };

  const dms = (at: number) => rational(at) + rational(at + 8) / 60 + rational(at + 16) / 3600;

  try {
    let gpsDir = -1;
    let exifDir = -1;
    readDir(tiff + u32(tiff + 4), (tag, _t, _c, valueAt) => {
      if (tag === 0x8825) gpsDir = tiff + u32(valueAt);
      if (tag === 0x8769) exifDir = tiff + u32(valueAt);
    });

    if (gpsDir > 0) {
      let latRef = "N";
      let lonRef = "E";
      readDir(gpsDir, (tag, _t, _c, valueAt) => {
        if (tag === 0x0001) latRef = String.fromCharCode(view.getUint8(valueAt));
        if (tag === 0x0003) lonRef = String.fromCharCode(view.getUint8(valueAt));
        if (tag === 0x0002) out.lat = dms(valueAt);
        if (tag === 0x0004) out.lon = dms(valueAt);
        if (tag === 0x0011) out.bearing = rational(valueAt); // GPSImgDirection
      });
      if (out.lat !== undefined && latRef === "S") out.lat = -out.lat;
      if (out.lon !== undefined && lonRef === "W") out.lon = -out.lon;
    }

    if (exifDir > 0) {
      readDir(exifDir, (tag, _t, count, valueAt) => {
        // DateTimeOriginal, "YYYY:MM:DD HH:MM:SS".
        if (tag !== 0x9003 || count < 10) return;
        let s = "";
        for (let i = 0; i < 10; i++) s += String.fromCharCode(view.getUint8(valueAt + i));
        const m = /^(\d{4}):(\d{2}):(\d{2})$/.exec(s);
        if (m) out.date = `${m[1]}-${m[2]}-${m[3]}`;
      });
    }
  } catch {
    // Malformed EXIF is common and is not the contributor's problem. The form
    // works without any of this.
    return {};
  }
  return out;
}

export function SubmitPhotoForm({
  parkId,
  parkName,
  centre,
}: {
  parkId: string;
  parkName: string;
  centre: { lat: number; lon: number };
}) {
  const [state, setState] = useState<State>({ kind: "idle" });
  const [file, setFile] = useState<File | null>(null);
  const [author, setAuthor] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [capturedOn, setCapturedOn] = useState("");
  const [bearing, setBearing] = useState(0);
  const [pin, setPin] = useState(centre);
  const [licenceAgreed, setLicenceAgreed] = useState(false);
  const [exifFound, setExifFound] = useState<string | null>(null);

  const mapRef = useRef<HTMLDivElement | null>(null);
  const markerRef = useRef<maplibregl.Marker | null>(null);

  useEffect(() => {
    if (!mapRef.current) return;
    const style =
      process.env.NEXT_PUBLIC_MAP_STYLE ?? "https://basemaps.cartocdn.com/gl/positron-gl-style/style.json";
    const map = new maplibregl.Map({
      container: mapRef.current,
      style,
      center: [centre.lon, centre.lat],
      zoom: 16,
    });
    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    const marker = new maplibregl.Marker({ draggable: true, color: "#1b6b3a" })
      .setLngLat([centre.lon, centre.lat])
      .addTo(map);
    marker.on("dragend", () => {
      const { lat, lng } = marker.getLngLat();
      setPin({ lat, lon: lng });
    });
    markerRef.current = marker;
    return () => map.remove();
  }, [centre]);

  // Keep the marker in step when EXIF moves the pin under it.
  useEffect(() => {
    markerRef.current?.setLngLat([pin.lon, pin.lat]);
  }, [pin]);

  async function onFile(f: File | null) {
    setFile(f);
    setExifFound(null);
    if (!f) return;
    // Only the first 128 KB: EXIF lives at the front, and reading a 12 MB file
    // into memory to find a header would be rude on a phone.
    const head = await f.slice(0, 131072).arrayBuffer();
    const exif = readExif(head);
    const found: string[] = [];
    if (exif.lat !== undefined && exif.lon !== undefined) {
      setPin({ lat: exif.lat, lon: exif.lon });
      found.push("where it was taken");
    }
    if (exif.bearing !== undefined) {
      setBearing(Math.round(exif.bearing) % 360);
      found.push("which way the camera pointed");
    }
    if (exif.date) {
      setCapturedOn(exif.date);
      found.push("the date");
    }
    setExifFound(found.length ? found.join(", ") : null);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) return setState({ kind: "error", message: "Choose a photograph." });
    setState({ kind: "submitting" });

    const body = new FormData();
    body.set("parkId", parkId);
    body.set("photo", file);
    body.set("author", author);
    body.set("contactEmail", email);
    body.set("note", note);
    body.set("capturedOn", capturedOn);
    body.set("lat", String(pin.lat));
    body.set("lon", String(pin.lon));
    body.set("bearingDeg", String(bearing));
    body.set("licenceAgreed", licenceAgreed ? "yes" : "no");

    try {
      const res = await fetch("/api/photo-submissions", { method: "POST", body });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) {
        setState({
          kind: "error",
          message:
            res.status === 429
              ? "That is a few submissions in a short time. Try again in a little while."
              : (json.error ?? "The upload did not go through. Check your connection and try again."),
        });
        return;
      }
      setState({ kind: "done", message: json.message ?? "Thank you." });
    } catch {
      setState({ kind: "error", message: "The upload did not go through. Check your connection and try again." });
    }
  }

  if (state.kind === "done") {
    return (
      <div className="rounded-xl border border-outline-variant bg-surface-container-lowest p-6">
        <h2 className="font-headline-md text-headline-md text-primary">Thank you</h2>
        <p className="mt-2 font-body-md text-body-md text-on-surface-variant">{state.message}</p>
        <p className="mt-4 font-body-md text-body-md text-on-surface-variant">
          Nothing appears on {parkName} until someone has looked at it. If you left an email address
          we will tell you either way.
        </p>
      </div>
    );
  }

  const busy = state.kind === "submitting";

  return (
    <form onSubmit={submit} className="space-y-6">
      <Field label="The photograph" required hint="JPEG, PNG, HEIC or WebP. Up to 12 MB.">
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif,image/webp"
            required
            disabled={busy}
            onChange={(e) => onFile(e.target.files?.[0] ?? null)}
            className={INPUT_CLASS + " py-2"}
          />
        )}
      </Field>

      {exifFound && (
        <p className="font-body-md text-body-md text-on-surface-variant">
          Read from the photograph: {exifFound}. Check it below and correct anything that is wrong.
        </p>
      )}

      <div className="space-y-2">
        <p className="font-body-md text-body-md font-semibold text-on-surface">
          Where were you standing? <span className="text-error">*</span>
        </p>
        <p className="font-body-md text-body-md text-on-surface-variant">
          Drag the pin. A photograph of a park with a viewpoint is evidence about a place; without
          one it is decoration.
        </p>
        <div ref={mapRef} className="h-72 w-full overflow-hidden rounded-xl border border-outline-variant" />
        <p className="font-body-md text-body-md text-on-surface-variant">
          {pin.lat.toFixed(5)}, {pin.lon.toFixed(5)}
        </p>
      </div>

      <div className="space-y-2">
        <label htmlFor="bearing" className="block font-body-md text-body-md font-semibold text-on-surface">
          Which way was the camera pointing? <span className="text-error">*</span>
        </label>
        <div className="flex items-center gap-4">
          <input
            id="bearing"
            type="range"
            min={0}
            max={359}
            value={bearing}
            disabled={busy}
            onChange={(e) => setBearing(Number(e.target.value))}
            className="h-10 flex-1"
          />
          <span
            aria-hidden
            style={{ transform: `rotate(${bearing}deg)` }}
            className="inline-block text-2xl leading-none transition-transform"
          >
            ↑
          </span>
          <span className="w-36 font-body-md text-body-md text-on-surface-variant">
            looking {cardinal(bearing)} ({bearing}&deg;)
          </span>
        </div>
      </div>

      <Field label="When was it taken?" required hint="A photograph of a since-rebuilt park is misleading, and the date is what makes that visible.">
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="date"
            required
            disabled={busy}
            max={new Date().toISOString().slice(0, 10)}
            value={capturedOn}
            onChange={(e) => setCapturedOn(e.target.value)}
            className={INPUT_CLASS}
          />
        )}
      </Field>

      <Field label="Who took it?" required hint="The licence you are granting requires us to credit you. This is shown on the park page.">
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="text"
            required
            maxLength={120}
            disabled={busy}
            value={author}
            onChange={(e) => setAuthor(e.target.value)}
            className={INPUT_CLASS}
          />
        )}
      </Field>

      <Field label="Email" hint="Optional, and never displayed. Only so we can tell you what happened to it.">
        {({ id, describedBy }) => (
          <input
            id={id}
            aria-describedby={describedBy}
            type="email"
            disabled={busy}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={INPUT_CLASS}
          />
        )}
      </Field>

      <Field label="Anything worth knowing" hint="Optional. &ldquo;The new splash pad&rdquo; is useful to whoever reviews it.">
        {({ id, describedBy }) => (
          <textarea
            id={id}
            aria-describedby={describedBy}
            rows={2}
            maxLength={500}
            disabled={busy}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full rounded-md border border-outline-variant bg-surface-container-lowest p-3 font-body-md text-body-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
          />
        )}
      </Field>

      <label className="flex gap-3 rounded-xl border border-outline-variant bg-surface-container-lowest p-4">
        <input
          type="checkbox"
          checked={licenceAgreed}
          disabled={busy}
          onChange={(e) => setLicenceAgreed(e.target.checked)}
          className="mt-1 h-4 w-4"
        />
        <span className="font-body-md text-body-md text-on-surface">
          I took this photograph, and I am licensing it under{" "}
          <a
            href="https://creativecommons.org/licenses/by/4.0/"
            target="_blank"
            rel="noopener noreferrer license"
            className="underline"
          >
            CC BY 4.0
          </a>{" "}
          so the Atlas and anyone else can use it with credit to me.
        </span>
      </label>

      {state.kind === "error" && (
        <p role="alert" className="font-body-md text-body-md text-error">
          {state.message}
        </p>
      )}

      <button
        type="submit"
        disabled={busy || !licenceAgreed}
        className="rounded-full bg-primary px-6 py-3 font-label-caps text-label-caps text-on-primary disabled:opacity-50"
      >
        {busy ? "Sending…" : "Submit this photograph"}
      </button>
      {!licenceAgreed && (
        <p className="font-body-md text-body-md text-on-surface-variant">
          We can only publish a photograph you took and are licensing to us, so the box above is
          required.
        </p>
      )}
    </form>
  );
}
